/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import Switch from '@ion/dls/src/components/switch/Switch';
import {LabelPlacement} from '../../components/switch/Switch';
import {LabelChipChooser} from '../../editors/LabelChipChooser';
import {LabelChooser} from '../../editors/LabelChooser';
import {
  LayerSettingsHeader,
  LayerSettingsProps,
  LayerSettingsStyles
} from './LayerSettings';

// defines the styles used by the CurrentLayerSettings
const useStyles = makeStyles(theme => ({
  ...LayerSettingsStyles(),
  details : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '96%',
    minWidth      : '225px'
  },
  accordionDetails : {
    display : 'block'
  }
}));

/**
 * @SubLayers
 *
 * Defines the SubLayers Tab / Section of the CurrentLayerSettings
 */
const SubLayers = () => {};

/**
 * @Legend
 *
 * Defines the Legend Tab / Section of the CurrentLayerSettings
 */
const Legend = () => {};

// defines the unique identifier for the CurrentLayerSettings
export const CURRENT_LAYER = 'current';

/**
 * @CurrentLayerSettings
 *
 * The Current Layer Settings provides a component which allows users to select
 * specific Current layer related attributes to be displayed within a DLSMap.
 *
 * The CurrentLayerSettings component represents the "Current Layers" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <CurrentLayerSettings onToggle={}
 *     isEnabled={}
 *     map={} />
 *
 * ```
 */
export const CurrentLayerSettings = ({
  map,
  layers,
  isEnabled,
  onToggle,
  expandedLayer,
  onToggleExpand,
  onToggleClustering,
  isClusteringActive,
  onToggleLabelling,
  isLabellingActive,
  labelContent,
  onChangeLabelContent,
  isLargeFleetMode
}: LayerSettingsProps) => {
  const classes: any                = useStyles();
  const [defaultLayer, setSelected] = useState(isEnabled);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  const translations = useMemo(() => (
    translate('maps:assets', {returnObjects: true})
  ), []);

  const onToggleLayer = useCallback(({target}) => {
    setSelected(target.checked);
    onToggle(CURRENT_LAYER, target.checked);
  }, [layers]);

  // Toggle Assets Clustering Feature
  const onToggleAssetClustering = useCallback(({target}) => {
    onToggleClustering(target.checked);
  }, []);

  // Toggle Assets Labelling Feature
  const onToggleAssetLabelling = useCallback(({target}) => {
    onToggleLabelling(target.checked);
  }, []);

  const Details = (
    <div className={classes.details}>
      {!isLargeFleetMode && (
        <Switch isDarkLabel={false}
          label={translations.clustering}
          labelPlacement={LabelPlacement.START}
          checked={isClusteringActive}
          onChange={onToggleAssetClustering} />
      )}
      <Switch isDarkLabel={false}
        label={translations.labelling}
        labelPlacement={LabelPlacement.START}
        checked={isLabellingActive}
        onChange={onToggleAssetLabelling} />
      <LabelChipChooser entityLabel={'assetLabel'}
        value={labelContent}
        label={translations.labelContent}
        disabled={!isLabellingActive}
        onChange={onChangeLabelContent} />
    </div>
  );

  const enabled = isEnabled !== defaultLayer ? isEnabled : defaultLayer;

  return (
    <div className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={translations.header}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              details={Details}
              header={(
                <LayerSettingsHeader label={translations.header}
                  isSelected={enabled}
                  onToggle={onToggleLayer}/>
              )}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default CurrentLayerSettings;
